import React, { useEffect } from "react";
import { useSelector } from "react-redux";
import { useDispatch } from "react-redux";
import { loadUser } from "../../../store/userSlice";
import { BsTelephone, BsThreeDotsVertical } from "react-icons/bs";
import { AnimatePresence, motion } from "framer-motion";
import { CiUser } from "react-icons/ci";
import { IoLockClosedOutline } from "react-icons/io5";
import { MdOutlineArrowForwardIos } from "react-icons/md";
import { MdOutlineMail } from "react-icons/md";
import { FiLogOut } from "react-icons/fi";
import { IoCopyOutline } from "react-icons/io5";
import { HiPencil } from "react-icons/hi";
import UserScreenWindow from "../Components/UserScreenWindow";
import { LogOutUser } from "../../../utils/firebase/Firebase.utils";
import Logo from "../../assets/Elegho-Logo.png";
import { useCompany } from "../../../context/CompanyContext";

import { getDocDataSnap } from "../../../utils/firebase/Firebase.utils";
import { Link, useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import { appURL } from "../../../utils/constants";

function Profile() {
  const currentUser = useSelector((state) => state.userObject);
  const { companyData } = useCompany();
  const dispatch = useDispatch();
  const [userData, setUserData] = React.useState({});
  const [showDropdown, setShowDropdown] = React.useState(false);

  const navigate = useNavigate();

  const handleLogout = () => {
    LogOutUser();

    dispatch(loadUser({}));
  };

  useEffect(() => {
    const fetchUserData = async () => {
      const user = await getDocDataSnap("users", currentUser.uid, (data) => {
        setUserData(data);
      });
    };
    fetchUserData();
  }, []);

  const invitationLink = `${appURL}/invite/${currentUser.uid}`;

  const handleCopy = () => {
    navigator.clipboard.writeText(invitationLink);
    toast.success("Link copied to clipboard");
  };

  const getInitials = (name) => {
    if (!name) return "JD";
    return name
      .split(" ")
      .map((word) => word[0])
      .join("")
      .toUpperCase()
      .slice(0, 2);
  };

  return (
    <UserScreenWindow label="Profile">
      <div className="w-full mx-auto px-2 py-4 space-y-4">
        {/* Profile Card */}
        <div className="bg-white rounded-xl shadow-sm p-4">
          <div className="flex items-start justify-between mb-4">
            <div className="flex items-center space-x-3">
              <div className="relative">
                <img
                  src={
                    userData.photoUrl ||
                    `https://api.dicebear.com/7.x/initials/svg?seed=${getInitials(
                      currentUser.displayName
                    )}&backgroundColor=363062`
                  }
                  alt="profile"
                  className="rounded-full h-16 w-16 object-cover object-center ring-2 ring-[#363062]/20"
                />
              </div>
              <div>
                <h2 className="text-base font-semibold text-[#363062]">
                  {currentUser.displayName || "John Doe"}
                </h2>
                {userData.linkedIdentifiers?.map((identifier) => (
                  <div key={identifier} className="text-xs text-[#363062]/70">
                    {identifier}
                  </div>
                ))}
              </div>
            </div>
            <div className="relative">
              <button
                className="p-2 hover:bg-gray-50 rounded-full"
                onClick={() => setShowDropdown(!showDropdown)}
              >
                <BsThreeDotsVertical className="text-[#363062]/70" />
              </button>

              {/* Dropdown Menu */}
              <AnimatePresence>
                {showDropdown && (
                  <motion.div
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -10 }}
                    className="absolute right-0 top-10 w-48 bg-white shadow-lg rounded-lg z-10 overflow-hidden"
                  >
                    <button
                      onClick={() => {
                        setShowDropdown(false);
                        navigate("/editprofile");
                      }}
                      className="flex items-center gap-2 w-full px-4 py-3 text-sm text-left hover:bg-[#363062]/5 transition-colors"
                    >
                      <HiPencil className="text-[#363062]" />
                      <span>Edit Profile</span>
                    </button>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </div>

          <div className="space-y-2">
            <div className="flex items-center gap-2 bg-[#363062]/5 px-3 py-2 rounded-lg">
              <MdOutlineMail className="text-[#363062] text-lg" />
              <span className="text-sm text-gray-700 truncate">
                {userData.email}
              </span>
            </div>
            <div className="flex items-center gap-2 bg-[#363062]/5 px-3 py-2 rounded-lg">
              <BsTelephone className="text-[#363062] text-lg" />
              <span className="text-sm text-gray-700">
                {userData.telephone || "No Phone Number"}
              </span>
            </div>
          </div>
        </div>

        {/* Navigation Menu */}
        <div className="bg-white rounded-xl shadow-sm overflow-hidden divide-y divide-gray-100">
          <Link to="/userprofile" className="block group">
            <div className="flex items-center justify-between p-4 hover:bg-[#363062]/5 transition-all">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-[#363062]/10 rounded-lg group-hover:bg-[#363062]/20">
                  <CiUser className="text-[#363062] text-lg" />
                </div>
                <div>
                  <span className="text-sm font-medium text-[#363062]">
                    Profile Settings
                  </span>
                  <span className="text-xs text-gray-500 block">
                    Update your personal information
                  </span>
                </div>
              </div>
              <MdOutlineArrowForwardIos className="text-[#363062]/40 group-hover:text-[#363062]" />
            </div>
          </Link>
          <div
            className="block group cursor-pointer"
            onClick={() => {
              navigate("/password");
            }}
          >
            <div className="flex items-center justify-between p-4 hover:bg-[#363062]/5 transition-all">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-[#363062]/10 rounded-lg group-hover:bg-[#363062]/20">
                  <IoLockClosedOutline className="text-[#363062] text-lg" />
                </div>
                <div>
                  <span className="text-sm font-medium text-[#363062]">
                    Password & Security
                  </span>
                  <span className="text-xs text-gray-500 block">
                    Manage your account security
                  </span>
                </div>
              </div>
              <MdOutlineArrowForwardIos className="text-[#363062]/40 group-hover:text-[#363062]" />
            </div>
          </div>
        </div>

        {/* Invitation Section */}
        {!currentUser.principalUser && (
          <div className="bg-white rounded-xl shadow-sm p-4">
            <h3 className="text-sm font-semibold text-[#363062] mb-2">
              Family Invitation
            </h3>
            <p className="text-xs text-gray-600 mb-3">
              Share this link with your family members to invite them
            </p>
            <div className="flex items-center gap-2">
              <input
                type="text"
                value={invitationLink}
                className="flex-1 bg-[#363062]/5 border border-[#363062]/10 rounded-lg px-3 py-2 text-xs text-gray-600"
                readOnly
              />
              <button
                onClick={handleCopy}
                className="flex items-center gap-1 bg-[#8FCD00] text-white px-3 py-2 rounded-lg hover:bg-opacity-90 transition-colors text-sm"
              >
                <IoCopyOutline className="text-sm" />
                Copy
              </button>
            </div>
          </div>
        )}

        {/* Logout Button */}
        <button
          onClick={handleLogout}
          className="w-full bg-white border border-red-200 text-red-500 rounded-xl p-3 flex items-center justify-center gap-2 hover:bg-red-50 transition-colors text-sm"
        >
          <FiLogOut className="text-sm" />
          <span>Log Out</span>
        </button>

        <div className="text-[8px] flex items-center justify-center gap-2 text-gray-500 mt-2">
          <div>Powered by</div>
          <a
            href="https://elegho.com"
            target="_blank"
            rel="noopener noreferrer"
            className="text-[#363062] text-xs font-semibold flex items-center gap-1"
          >
            <img src={Logo} alt="Elegho Logo" className="h-3" />
          </a>
          <div>ver 1.90.01</div>
        </div>
      </div>
    </UserScreenWindow>
  );
}

export default Profile;
